import { useMutation } from '@apollo/client';
import { produce } from 'immer';
import { useState } from 'react';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import { useCopyContentElement } from '@eluve/blocks';
import { Text, useToast } from '@eluve/components';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@eluve/components';
import {
  appointmentAmendments,
  billingCodeAmendmentFragment,
  externalChartsFragment,
  useAmendmentActivity,
  useAmendments,
  useAppointmentId,
  useIsUserAppointmentOwner,
  useSummaryContentRef,
} from '@eluve/frontend-appointment-hooks';
import {
  appointmentBillingCodesFragment,
  useEditBillingCodes,
  useGetFilePdfUrl,
} from '@eluve/frontend-feature-appointment';
import { graphql } from '@eluve/graphql.tada';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

const SignedAppointmentDetailsFragment = graphql(
  `
    fragment SignedAppointmentDetails on Appointments {
      __typename
      id
      doctor_interaction {
        appointmentId
        __typename
        noteSignedAt
        user {
          __typename
          id
          firstName
          lastName
        }
      }
      ...externalCharts
    }
  `,
  [externalChartsFragment],
);

const createAmendmentMutation = graphql(`
  mutation createAmendment($appointmentId: uuid!) {
    insertAmendmentsOne(object: { appointmentId: $appointmentId }) {
      __typename
      id
      appointmentId
      user {
        __typename
        id
        firstName
        lastName
        email
      }
    }
  }
`);

const cancelAmendmentMutation = graphql(`
  mutation cancelAmendment($id: uuid!, $tenantId: uuid!) {
    deleteAmendmentsByPk(id: $id, tenantId: $tenantId) {
      __typename
      id
    }
  }
`);

const submitAmendmentMutation = graphql(`
  mutation updateAmendment($id: uuid!, $tenantId: uuid!) {
    updateAmendmentsByPk(
      pkColumns: { id: $id, tenantId: $tenantId }
      _set: { submittedAt: NOW }
    ) {
      __typename
      id
      submittedAt
    }
  }
`);

export const SignedSummaryFooter: React.FC = () => {
  const toast = useToast();
  const { contentRef } = useSummaryContentRef();
  const appointmentId = useAppointmentId();
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const isUserApptOwner = useIsUserAppointmentOwner();
  const [tooltipContent, setTooltipContent] = useState('Copy to clipboard');
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const analytics = useAnalytics('amendments');
  const { amendments } = useAmendments(appointmentId);
  const { refetchBillingCodes } = useEditBillingCodes({
    appointmentId,
    tenantId,
  });

  const [createAmendment] = useMutation(createAmendmentMutation, {
    onError: () => {
      toast.error('Failed to create amendment');
    },
    onCompleted: (data) => {
      if (data?.insertAmendmentsOne) {
        analytics.capture('user_entered_amendment_mode', {
          appointmentId,
          userId,
          tenantId,
          amendmentId: data.insertAmendmentsOne.id,
        });
      }
    },
    optimisticResponse: () => ({
      insertAmendmentsOne: {
        __typename: 'Amendments' as const,
        id: 'not-created-yet',
        appointmentId,
        user: {
          __typename: 'Users' as const,
          id: userId,
          firstName: '',
          lastName: '',
          email: '',
        },
      },
    }),
    update: (_, { data }) => {
      cacheUtils.updateFragment(
        {
          fragment: appointmentAmendments,
          key: { id: appointmentId },
        },
        (existing) => {
          if (!existing || !data?.insertAmendmentsOne) {
            return null;
          }
          return produce(existing, (draft) => {
            draft.amendments.push({
              __typename: 'Amendments' as const,
              id: data.insertAmendmentsOne!.id,
              appointmentId,
              tenantId,
              userId,
              submittedAt: null,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              chart_amendments: [],
              billing_code_amendment: null,
              user: {
                __typename: 'Users' as const,
                id: userId,
                firstName: '',
                lastName: '',
                email: '',
              },
            });
          });
        },
      );
    },
  });

  const { activeAmendment } = useAmendmentActivity();

  const [cancelAmendment] = useMutation(cancelAmendmentMutation, {
    onError: () => toast.error('Failed to cancel amendment'),
    optimisticResponse: (data) => ({
      deleteAmendmentsByPk: {
        __typename: 'Amendments' as const,
        id: data.id,
      },
    }),
    update: () => {
      cacheUtils.evict({
        typeName: 'Amendments',
        key: { id: activeAmendment!.id },
      });
    },
    onCompleted: () => {
      // deleting an amendment triggers a delete of potential billing
      // code amendments, so we should refetch the billing codes just
      // to be sure we have the latest data
      refetchBillingCodes();
    },
  });

  const [submitAmendment] = useMutation(submitAmendmentMutation, {
    onError: () => toast.error('Failed to submit amendment'),
    optimisticResponse: (data) => ({
      updateAmendmentsByPk: {
        __typename: 'Amendments' as const,
        id: data.id,
        submittedAt: new Date().toISOString(),
      },
    }),
    update: () => {
      if (!activeAmendment?.billing_code_amendment) {
        return;
      }

      const existingBillingCodes = cacheUtils.readFragment({
        fragment: appointmentBillingCodesFragment,
        key: { id: appointmentId },
      });

      // write billing code amendment to cache if needed
      cacheUtils.writeFragment({
        fragment: billingCodeAmendmentFragment,
        key: { id: activeAmendment!.billing_code_amendment!.id },
        data: {
          __typename: 'BillingCodeAmendments' as const,
          id: activeAmendment!.billing_code_amendment!.id,
          tenantId,

          amendmentId: activeAmendment!.id,
          createdAt: new Date().toISOString(),

          billing_codes: existingBillingCodes.billingCodes.map((code) => ({
            __typename: 'AppointmentBillingCodesSnapshots' as const,
            id: code.id,
            appointmentId,
            tenantId,
            description: code.description,
            medical_code: code.medical_code,
          })),
        },
      });
    },
  });

  const { amendments: draftAmendments } = useAmendments(appointmentId, {
    draftOnly: true,
  });
  const isChartAmendmentEnabled = useIsFeatureFlagEnabled('CHART_AMENDMENTS');
  const isPdfEnabled = useIsFeatureFlagEnabled('PDF_OUTPUT');
  const currentDraftAmendment = draftAmendments[0];

  const scrollSummaryToTop = () => {
    if (contentRef) {
      let scrollableParent = contentRef.parentElement;
      while (scrollableParent) {
        const style = window.getComputedStyle(scrollableParent);
        const hasScrollableContent =
          style.overflowY === 'auto' ||
          style.overflowY === 'scroll' ||
          scrollableParent === document.documentElement;
        if (hasScrollableContent) {
          scrollableParent.scrollTo({ top: 0, behavior: 'smooth' });
          break;
        }
        scrollableParent = scrollableParent.parentElement;
      }
      if (!scrollableParent) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const amend = () => {
    scrollSummaryToTop();

    return createAmendment({
      variables: {
        appointmentId,
      },
    });
  };

  const cancelActiveAmendment = () => {
    analytics.capture('user_discarded_amendment', {
      appointmentId,
      userId,
      tenantId,
      amendmentId: currentDraftAmendment!.id,
    });

    return cancelAmendment({
      variables: {
        id: currentDraftAmendment!.id,
        tenantId,
      },
    });
  };

  const submitActiveAmendment = () => {
    scrollSummaryToTop();

    analytics.capture('user_signed_amendment', {
      appointmentId,
      userId,
      tenantId,
      amendmentId: currentDraftAmendment!.id,
      // A chart that has been signed once chartVersion = 1,
      // signed again with an amendment = 2,
      // signed again with another amendment = 3
      // and so on.
      chartVersion: amendments.length + 1,
    });

    return submitAmendment({
      variables: {
        id: currentDraftAmendment!.id,
        tenantId,
      },
    });
  };

  const appointment = useCompleteFragment({
    fragment: SignedAppointmentDetailsFragment,
    key: {
      id: appointmentId,
    },
  });

  const copySummary = useCopyContentElement({
    element: contentRef,
    onCopied: () => {
      setTooltipContent('Copied!');
      setTooltipOpen(true);
      setTimeout(() => setTooltipContent('Copy to clipboard'), 3000);
    },
  });

  const handlePdf = useGetFilePdfUrl({
    appointmentId,
    tenantId,
    type: 'CHART',
  });

  const openPdf = async () => {
    const newTab = window.open('about:blank', '_blank');
    const response = await handlePdf();
    if (response.status === 201 && newTab) {
      newTab.location = response.body.pdfUrl;
    } else {
      toast.error('Failed to prepare PDF');
    }
  };

  if (!appointment.doctor_interaction?.noteSignedAt) {
    return null;
  }

  const canAmendChart = isChartAmendmentEnabled && isUserApptOwner;
  const showDropdownMenu = canAmendChart || isPdfEnabled;

  return (
    <Stack justify="end" gap={2} direction="horizontal">
      <Stack direction="horizontal">
        <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              className={cn('w-full', showDropdownMenu && 'rounded-r-none')}
              onClick={copySummary}
            >
              <Text color="secondary">Copy Summary</Text>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{tooltipContent}</TooltipContent>
        </Tooltip>
        {showDropdownMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-l-none">
                <Icon name="ChevronDown" size="lg" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top">
              {canAmendChart && currentDraftAmendment && (
                <DropdownMenuItem
                  className="flex cursor-pointer items-center gap-2"
                  onClick={cancelActiveAmendment}
                >
                  <Text color="secondary">Discard Amendment</Text>
                </DropdownMenuItem>
              )}
              {canAmendChart && !currentDraftAmendment && (
                <DropdownMenuItem
                  className="flex cursor-pointer items-center gap-2"
                  onClick={amend}
                >
                  <Text color="secondary">Amend Chart</Text>
                </DropdownMenuItem>
              )}
              {isPdfEnabled && (
                <DropdownMenuItem
                  className="flex cursor-pointer items-center gap-2"
                  onClick={openPdf}
                >
                  <Text color="secondary">Preview PDF</Text>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </Stack>

      {isChartAmendmentEnabled && currentDraftAmendment && isUserApptOwner && (
        <Button onClick={submitActiveAmendment}>Sign Note</Button>
      )}
    </Stack>
  );
};
